/**
 * Custom fetch mutator wired into orval's generated React Query hooks.
 *
 * Responsibilities:
 *   - inject Bearer JWT from auth store
 *   - inject X-Tenant-Slug from tenant context (subdomain detection)
 *   - bubble 401 → auth refresh, 403 → capability error
 *
 * Read by orval at generation time (see orval.config.ts). Safe to import
 * standalone for manual API calls before generated hooks exist.
 */
export interface FetchOptions extends Omit<RequestInit, 'body'> {
  url: string;
  params?: Record<string, string | number | boolean | undefined>;
  data?: unknown;
  baseURL?: string;
  /** Не отправлять Authorization (публичные /v1/public/* эндпойнты). */
  noAuth?: boolean;
}

export interface ApiError extends Error {
  status: number;
  body: unknown;
}

let authTokenProvider: () => string | null = () => null;
let tenantSlugProvider: () => string | null = () => null;

export function setAuthTokenProvider(fn: () => string | null): void {
  authTokenProvider = fn;
}
export function setTenantSlugProvider(fn: () => string | null): void {
  tenantSlugProvider = fn;
}

let refreshHandler: () => Promise<boolean> = async () => false;
let onAuthFailure: () => void = () => {};

/** Хендлер обновления access-токена по refresh-токену. true при успехе. */
export function setRefreshHandler(fn: () => Promise<boolean>): void {
  refreshHandler = fn;
}
/** Колбэк при неустранимом 401 (refresh не помог) — обычно редирект на /login. */
export function setOnAuthFailure(fn: () => void): void {
  onAuthFailure = fn;
}

// single-flight: пачка конкурентных 401 делит один refresh (иначе ротация
// refresh-токена побьёт сессию параллельными обновлениями).
let inflightRefresh: Promise<boolean> | null = null;
function runRefresh(): Promise<boolean> {
  if (!inflightRefresh) {
    inflightRefresh = refreshHandler().catch(() => false);
    void inflightRefresh.then(
      () => {
        inflightRefresh = null;
      },
      () => {
        inflightRefresh = null;
      },
    );
  }
  return inflightRefresh;
}

export async function customFetch<T>(opts: FetchOptions): Promise<T> {
  const { url, params, data, baseURL = '/api/v1', headers, noAuth = false, ...rest } = opts;

  const qs = params
    ? '?' +
      new URLSearchParams(
        Object.entries(params).flatMap(([k, v]) =>
          v === undefined ? [] : [[k, String(v)]],
        ),
      ).toString()
    : '';

  const finalHeaders = new Headers(headers ?? {});
  finalHeaders.set('Accept', 'application/json');
  // FormData → НЕ выставляем Content-Type: fetch сам проставит multipart с
  // boundary (иначе сервер не распарсит тело). Для прочих тел — JSON.
  const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;
  if (data !== undefined && !isFormData && !finalHeaders.has('Content-Type')) {
    finalHeaders.set('Content-Type', 'application/json');
  }
  const token = noAuth ? null : authTokenProvider();
  if (token) finalHeaders.set('Authorization', `Bearer ${token}`);
  const slug = tenantSlugProvider();
  if (slug) finalHeaders.set('X-Tenant-Slug', slug);

  const init: RequestInit = { ...rest, headers: finalHeaders };
  if (data !== undefined) init.body = isFormData ? (data as FormData) : JSON.stringify(data);

  const fullUrl = `${baseURL}${url}${qs}`;
  const isAuthCall = url.includes('/auth/login') || url.includes('/auth/refresh');
  let res = await fetch(fullUrl, init);

  // 401 → одна попытка обновить токен и повтор запроса; иначе — на /login.
  // Только 401 запускает onAuthFailure (логаут/редирект). 403 («нет прав на
  // ресурс») НИКОГДА не чистит токены и не логаутит — это ошибка доступа к
  // конкретному эндпойнту, а не смерть сессии; пробрасываем как ApiError(403),
  // экран показывает «нет доступа».
  if (res.status === 401 && !noAuth && !isAuthCall) {
    const refreshed = await runRefresh();
    if (refreshed) {
      const fresh = authTokenProvider();
      if (fresh) finalHeaders.set('Authorization', `Bearer ${fresh}`);
      res = await fetch(fullUrl, { ...init, headers: finalHeaders });
    } else {
      onAuthFailure();
    }
  }

  const ct = res.headers.get('content-type') ?? '';
  const body: unknown = ct.includes('application/json')
    ? await res.json()
    : await res.text();

  if (!res.ok) {
    const err = new Error(`API ${res.status} ${res.statusText}`) as ApiError;
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body as T;
}
